<!-- Suppressed when embedded: the host panel supplies the title and the patient name.
     Rendering both put the same name on screen twice, one above the other. -->
<!-- As a dialog, in the chat window's frame (src/_cura-window.scss, panel class
     ai-recording-panel); embedded in the review wizard the host supplies the title. -->
<div class="cw-header" *ngIf="!embedded">
    <mat-icon class="kind">{{ historyOnlyCode ? 'history' : 'rule' }}</mat-icon>
    <div class="title">
        <span class="name">{{ historyOnlyCode ? historyOnlyCode + ' — coding history' : 'ICD-10 coding suggestions' }}</span>
        <span class="subtitle">{{ patientName }}</span>
    </div>
</div>

<mat-dialog-content class="icd-dialog-content" [class.embedded]="embedded" [class.cw-body]="!embedded">

    <!-- History-only mode: opened from a chip, showing what happened to that one code. -->
    <ng-container *ngIf="historyOnlyCode">
        <p class="hint intro">
            Every change to this code - what happened, when, and who or what did it.
        </p>
        <div class="audit-list" *ngIf="auditLoading === historyOnlyCode">
            <span class="hint">Loading history&hellip;</span>
        </div>
        <div class="audit-list" *ngIf="auditByCode[historyOnlyCode]">
            <p class="hint" *ngIf="auditByCode[historyOnlyCode].length === 0">
                No recorded history for this code.
            </p>
            <div class="audit-entry" *ngFor="let a of auditByCode[historyOnlyCode]">
                <span class="audit-date">{{ a.createdAt | date:'d MMM y, HH:mm' }}</span>
                <span class="audit-action">{{ a.action }}</span>
                <span class="audit-actor" [class.ai]="a.actorType === 'ai-auto'">{{ a.actorType }}</span>
                <span class="audit-reason" *ngIf="a.reason">{{ a.reason }}</span>
            </div>
        </div>
    </ng-container>

    <p class="hint intro" *ngIf="!historyOnlyCode">
        Suggested from this patient's clinical notes. Every suggestion cites a verbatim quote from
        the record - nothing is proposed that cannot be pointed at.
    </p>

    <div class="loading-row" *ngIf="!historyOnlyCode && loading">
        <mat-spinner diameter="28"></mat-spinner>
        <span>Loading suggestions&hellip;</span>
    </div>

    <p class="error-text" *ngIf="!historyOnlyCode && loadError">{{ loadError }}</p>

    <p class="hint" *ngIf="!historyOnlyCode && !loading && !loadError && suggestions.length === 0">
        No suggestions are waiting for this patient.
    </p>

    <!-- ══ The proposed coding set ══════════════════════════════════════════════
         The record as it would stand with everything below accepted. The ICD-10 rules that
         matter here — exactly one primary, a dagger with its asterisk, an injury with its
         external cause — are properties of the SET, and cannot be seen in a list of edits. -->
    <div class="coding-set" *ngIf="!historyOnlyCode && !loading && !loadError && suggestions.length">
        <h3 class="section-title">Proposed coding set</h3>
        <p class="hint">
            How this patient's coding would read once the decisions below are made.
        </p>

        <div class="set-row" *ngFor="let r of codingSet" [ngClass]="r.state">
            <span class="set-marker" [ngClass]="r.state"></span>
            <span class="code">{{ r.code }}</span>
            <span class="description">{{ r.description }}</span>
            <span class="set-role" *ngIf="roleLabel(r.newRole || r.role)">{{ roleLabel(r.newRole || r.role) }}</span>
            <span class="set-state" [ngClass]="r.state">{{ stateLabel(r) }}</span>
        </div>

        <!-- §7: exactly one primary. Silent when the answer is unknowable — almost every code
             already on the record predates `codeRole` being captured, so "no primary" could not
             be distinguished from "no role recorded", and the banner was wrong on nearly every
             patient. More than one primary IS knowable, and is still reported. -->
        <p class="set-warning" *ngIf="primaryWarning()">{{ primaryWarning() }}</p>

        <div class="bulk-row" *ngIf="bulkAcceptable().length">
            <button mat-raised-button color="primary" class="tone-button" [disabled]="bulkRunning" (click)="acceptAll()">
                Accept all {{ bulkAcceptable().length }} additions
            </button>
            <span class="hint">
                Removals, replacements and choices are left for you - those are never applied in bulk.
            </span>
        </div>
        <p class="card-message" *ngIf="bulkMessage">{{ bulkMessage }}</p>
    </div>

    <h3 class="section-title" *ngIf="!historyOnlyCode && !loading && !loadError && decisions.length">
        Decisions
    </h3>

    <!-- ══ Coupled decisions ════════════════════════════════════════════════════
         A dagger accepted without its asterisk is an invalid record, so the UI must not offer
         that as a click. The server enforces the same coupling at the write; this makes the
         buttons describe what they actually do. -->
    <div class="decision-card coupled" *ngFor="let d of coupledDecisions" [ngClass]="d.kind"
        [class.removal]="d.lead.action === 'remove'">

        <ng-container>
            <p class="coupling-note" [ngClass]="d.kind">{{ decisionTitle(d) }}</p>

            <div class="member" *ngFor="let m of d.members; let i = index">
                <div class="suggestion-header">
                    <span class="action-chip" [ngClass]="m.action">{{ actionLabel(m.action) }}</span>
                    <span class="code">{{ m.code }}</span>
                    <span class="description">{{ m.description }}</span>
                    <span class="pmb-chip" *ngIf="m.pmbCodes" matTooltip="Prescribed Minimum Benefit">PMB</span>
                    <span class="recommended" *ngIf="d.kind === 'exclusive' && i === 0">More specific</span>
                </div>

                <p class="rationale" *ngIf="m.rationale">{{ m.rationale }}</p>

                <div class="evidence" *ngFor="let e of quotesFor(m)">
                    <span class="quote">&ldquo;{{ e.quote }}&rdquo;</span>
                    <span class="quote-meta">
                        <span *ngIf="e.noteDate">note of {{ e.noteDate }}</span>
                        <span class="source-chip" *ngIf="e.source && e.source !== 'note'"
                            [class.inferred]="isInferredSource(e.source)"
                            [matTooltip]="isInferredSource(e.source)
                                ? 'This text is an AI description of an image, not a reported finding'
                                : 'Transcribed from an attached report'">
                            {{ e.source }}
                        </span>
                    </span>
                </div>

                <div class="meta-row">
                    <span class="confidence">Confidence {{ (m.confidence * 100) | number:'1.0-0' }}%</span>
                    <span class="verdict" [class.agree]="m.verifierVerdict === 'agree'"
                        [matTooltip]="m.verifierRationale || ''">{{ verdictLabel(m.verifierVerdict) }}</span>
                    <span class="claim-warning" *ngIf="m.pairingWarning"
                        matTooltip="This code is documented with a partner code in the SA codebook">
                        Pairs with {{ m.pairingWarning }}
                    </span>
                    <button mat-button class="history-link" (click)="toggleAudit(m.code)">
                        {{ auditByCode[m.code] ? 'Hide history' : 'History' }}
                    </button>
                </div>

                <div class="audit-list" *ngIf="auditByCode[m.code]">
                    <p class="hint" *ngIf="auditByCode[m.code].length === 0">No history for this code yet.</p>
                    <div class="audit-entry" *ngFor="let a of auditByCode[m.code]">
                        <span class="audit-date">{{ a.createdAt | date:'d MMM y' }}</span>
                        <span class="audit-action">{{ a.action }}</span>
                        <span class="audit-actor" [class.ai]="a.actorType === 'ai-auto'">{{ a.actorType }}</span>
                        <span class="audit-reason" *ngIf="a.reason">{{ a.reason }}</span>
                    </div>
                </div>

                <!-- A choice is decided per arm: picking one is declining the other, which the
                     server does as part of the same write. -->
                <div class="card-actions" *ngIf="d.kind === 'exclusive' && rejectingId !== d.key">
                    <button mat-raised-button color="primary" class="tone-button" [disabled]="isBusy(d.key)"
                        (click)="acceptDecision(d, m)">
                        Use {{ m.code }}
                    </button>
                </div>
            </div>

            <p class="card-message" *ngIf="d.message">{{ d.message }}</p>

            <div class="reject-form" *ngIf="rejectingId === d.key">
                <mat-form-field appearance="outline" class="full-width">
                    <mat-label>Reason (optional)</mat-label>
                    <input matInput [(ngModel)]="rejectReason" placeholder="e.g. resolved after surgery in March">
                </mat-form-field>
                <p class="hint">
                    All {{ d.members.length }} codes here will be dismissed together, and will not be
                    suggested again unless new clinical evidence appears in the notes.
                </p>
                <div class="card-actions">
                    <button mat-button class="quiet-button" (click)="cancelReject()">Cancel</button>
                    <button mat-raised-button color="warn" [disabled]="isBusy(d.key)"
                        (click)="confirmReject(d)">Dismiss both</button>
                </div>
            </div>

            <div class="card-actions" *ngIf="rejectingId !== d.key">
                <button mat-button class="quiet-button" [disabled]="isBusy(d.key)" (click)="startReject(d)">
                    {{ d.kind === 'exclusive' ? 'Neither' : 'Dismiss both' }}
                </button>
                <button mat-raised-button color="primary" class="tone-button" *ngIf="d.kind === 'together'"
                    [disabled]="isBusy(d.key)" (click)="acceptDecision(d)">
                    Accept both
                </button>
            </div>
        </ng-container>
    </div>

    <!-- Uncoupled suggestions: one code, one decision, per-line accept/dismiss as before.
         `s` is the single member; the decision wrapper carries the busy/reject key so both
         card kinds go through the same accept path. -->
    <div class="suggestion-card" *ngFor="let d of singleDecisions"
        [class.removal]="d.lead.action === 'remove'">
      <ng-container *ngIf="d.lead as s">

        <div class="suggestion-header">
            <span class="action-chip" [ngClass]="s.action">{{ actionLabel(s.action) }}</span>
            <span class="code">{{ s.code }}</span>
            <span class="description">{{ s.description }}</span>
            <span class="pmb-chip" *ngIf="s.pmbCodes" matTooltip="Prescribed Minimum Benefit">PMB</span>
        </div>

        <p class="hint removal-note" *ngIf="s.action === 'remove'">
            This code is flagged on the record. It is never removed automatically - only you
            can decide that.
        </p>

        <p class="episode" *ngFor="let ep of s.episodes">
            Episode: {{ ep.startDate }}<span *ngIf="ep.endDate"> - {{ ep.endDate }}</span>
            <span *ngIf="ep.type"> ({{ ep.type }})</span>
        </p>
        <p class="hint" *ngIf="s.ids.length > 1">
            Proposed for {{ s.ids.length }} episodes of care. Deciding once resolves all of them.
        </p>

        <p class="rationale" *ngIf="s.rationale">{{ s.rationale }}</p>

        <!-- The evidence gate is the core safety property: every suggestion must cite a quote
             that was string-matched against the actual note text. Showing it is what makes the
             suggestion checkable rather than something to be trusted. -->
        <!-- Provenance matters here. Evidence may come from the note, from a transcribed
             report (a pathologist's stated diagnosis), or from an AI description of an image.
             The last is a model inference, not a recorded finding — a clinician deciding on
             this suggestion needs to see which they are being asked to trust. -->
        <div class="evidence" *ngFor="let e of quotesFor(s)">
            <span class="quote">&ldquo;{{ e.quote }}&rdquo;</span>
            <span class="quote-meta">
                <span *ngIf="e.noteDate">note of {{ e.noteDate }}</span>
                <span class="source-chip" *ngIf="e.source && e.source !== 'note'"
                    [class.inferred]="isInferredSource(e.source)"
                    [matTooltip]="isInferredSource(e.source)
                        ? 'This text is an AI description of an image, not a reported finding'
                        : 'Transcribed from an attached report'">
                    {{ e.source }}
                </span>
            </span>
        </div>

        <div class="meta-row">
            <span class="confidence">Confidence {{ (s.confidence * 100) | number:'1.0-0' }}%</span>
            <span class="verdict" [class.agree]="s.verifierVerdict === 'agree'"
                [matTooltip]="s.verifierRationale || ''">{{ verdictLabel(s.verifierVerdict) }}</span>
            <span class="claim-warning" *ngIf="s.affectsSubmittedClaim">
                Affects a submitted claim
            </span>
            <!-- §4.5: SA mandates place AND activity for chapter XX. An injury accepted
                 without its paired external cause is a claim rejection waiting to happen. -->
            <span class="claim-warning" *ngIf="s.needsExternalCausePair"
                matTooltip="South African coding requires an external-cause code (V-Y) alongside an injury">
                Needs an external-cause code
            </span>
            <!-- The MIT dagger/asterisk flags are non-exhaustive, so this warns and never
                 blocks: a partner the codebook does not name is not an error. -->
            <span class="claim-warning" *ngIf="s.pairingWarning"
                matTooltip="This code is documented with a partner code in the SA codebook">
                Pairs with {{ s.pairingWarning }}
            </span>
            <button mat-button class="history-link" (click)="toggleAudit(s.code)">
                {{ auditByCode[s.code] ? 'Hide history' : 'History' }}
            </button>
        </div>

        <div class="audit-list" *ngIf="auditLoading === s.code">
            <span class="hint">Loading history&hellip;</span>
        </div>
        <div class="audit-list" *ngIf="auditByCode[s.code]">
            <p class="hint" *ngIf="auditByCode[s.code].length === 0">No history for this code yet.</p>
            <div class="audit-entry" *ngFor="let a of auditByCode[s.code]">
                <span class="audit-date">{{ a.createdAt | date:'d MMM y' }}</span>
                <span class="audit-action">{{ a.action }}</span>
                <span class="audit-actor" [class.ai]="a.actorType === 'ai-auto'">{{ a.actorType }}</span>
                <span class="audit-reason" *ngIf="a.reason">{{ a.reason }}</span>
            </div>
        </div>

        <p class="card-message" *ngIf="d.message">{{ d.message }}</p>

        <!-- Reject confirmation. §7.1 — dismissal suppresses the code until new evidence
             arrives, so the consequence is stated before the click, not after. -->
        <div class="reject-form" *ngIf="rejectingId === d.key">
            <mat-form-field appearance="outline" class="full-width">
                <mat-label>Reason (optional)</mat-label>
                <input matInput [(ngModel)]="rejectReason" placeholder="e.g. resolved after surgery in March">
            </mat-form-field>
            <p class="hint">
                This code will not be suggested again for this episode unless new clinical evidence
                appears in the notes.
            </p>
            <div class="card-actions">
                <button mat-button class="quiet-button" (click)="cancelReject()">Cancel</button>
                <button mat-raised-button color="warn" [disabled]="isBusy(d.key)"
                    (click)="confirmReject(d)">Dismiss</button>
            </div>
        </div>

        <div class="card-actions" *ngIf="rejectingId !== d.key">
            <button mat-button class="quiet-button" [disabled]="isBusy(d.key)" (click)="startReject(d)">Dismiss</button>
            <button mat-raised-button color="primary" class="tone-button" [disabled]="isBusy(d.key)" (click)="acceptDecision(d)">
                {{ s.action === 'remove' ? 'Remove code' : 'Accept' }}
            </button>
        </div>
      </ng-container>
    </div>

</mat-dialog-content>

<!-- Suppressed when embedded: `dialogRef` is null there, so this button would do nothing at
     all, and the host already owns closing. -->
<mat-dialog-actions class="cw-actions" *ngIf="!embedded">
    <span class="spacer"></span>
    <button mat-stroked-button class="quiet-button" (click)="close()">Close</button>
</mat-dialog-actions>
